import {
  SchemaDatapoint,
  SchemaItem,
  SchemaSection,
} from '@rossum/api-client/schemas';

const mapSchemaNode = <T extends SchemaItem | SchemaSection>(
  item: T,
  fieldSchemaId: string,
  transform: (datapoint: SchemaDatapoint) => SchemaDatapoint
): T => {
  if (item.category === 'datapoint' && item.id === fieldSchemaId) {
    return transform(item) as T;
  }

  if (item.category === 'multivalue') {
    return {
      ...item,
      children: mapSchemaNode(item.children, fieldSchemaId, transform),
    };
  }

  if ('children' in item) {
    return {
      ...item,
      children: item.children.map(child =>
        mapSchemaNode(child, fieldSchemaId, transform)
      ),
    };
  }

  return item;
};

/**
 * Recursively traverses schema content and applies a transform function
 * to the datapoint matching the given field ID.
 *
 * Works with any nesting level - handles future schema structure changes.
 *
 * @param schemaContent - The schema sections to traverse
 * @param fieldSchemaId - The ID of the field to transform
 * @param transform - Function to apply to the matching datapoint
 * @returns New schema content with the transformed field
 */
export const mapSchemaField = (
  schemaContent: SchemaSection[],
  fieldSchemaId: string,
  transform: (datapoint: SchemaDatapoint) => SchemaDatapoint
): SchemaSection[] => {
  return schemaContent.map(section =>
    mapSchemaNode(section, fieldSchemaId, transform)
  );
};
